import { useState } from "react";
import { Users, Calendar, Loader2, Copy, Printer, Sparkles } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";

interface Briefing {
  overview?: string;
  relationshipHistory?: string;
  activeDeals?: string[];
  portfolioPositions?: string[];
  talkingPoints?: string[];
  risksAndFlags?: string[];
  rawContext?: string;
}

export default function MeetingPrep() {
  const { orgId, familyId } = useTenant();
  const [counterpartyName, setCounterpartyName] = useState("");
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(false);
  const [briefing, setBriefing] = useState<Briefing | null>(null);

  const generateBriefing = async () => {
    if (!counterpartyName.trim()) {
      toast.error("Please enter a counterparty name");
      return;
    }

    setLoading(true);
    setBriefing(null);

    try {
      const { data, error } = await supabase.functions.invoke("meeting-prep-agent", {
        body: { counterpartyName, meetingDate, orgId, familyId },
      });

      if (error) throw error;
      setBriefing(data);
      toast.success("Briefing generated");
    } catch (err) {
      toast.error("Failed to generate briefing: " + (err instanceof Error ? err.message : "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const copyBriefing = () => {
    if (!briefing) return;
    const text = [
      `Meeting Briefing: ${counterpartyName}`,
      `Date: ${meetingDate}`,
      "",
      briefing.overview || "",
      "",
      "Talking Points:",
      ...(briefing.talkingPoints || []).map((tp, i) => `${i + 1}. ${tp}`),
    ].join("\n");
    navigator.clipboard.writeText(text);
    toast.success("Briefing copied to clipboard");
  };

  return (
    <div className="flex-1 space-y-6 px-4 py-6">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Meeting Prep Agent</h1>
          <p className="text-muted-foreground">AI-powered meeting briefings from your portfolio data</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Generate Briefing
          </CardTitle>
          <CardDescription>
            Enter the counterparty or contact details to generate a meeting briefing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Counterparty / Fund Manager</Label>
              <Input
                placeholder="e.g., Blackstone, KKR, Goldman Sachs"
                value={counterpartyName}
                onChange={(e) => setCounterpartyName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Meeting Date</Label>
              <Input
                type="date"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
              />
            </div>
          </div>
          <Button onClick={generateBriefing} disabled={loading}>
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            {loading ? "Generating..." : "Generate Briefing"}
          </Button>
        </CardContent>
      </Card>

      {briefing && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Briefing: {counterpartyName}</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={copyBriefing}>
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.print()}>
                <Printer className="mr-2 h-4 w-4" />
                Print
              </Button>
            </div>
          </div>

          {briefing.overview && (
            <Card>
              <CardHeader><CardTitle className="text-lg">Overview</CardTitle></CardHeader>
              <CardContent><p className="text-sm">{briefing.overview}</p></CardContent>
            </Card>
          )}

          {briefing.relationshipHistory && (
            <Card>
              <CardHeader><CardTitle className="text-lg">Relationship History</CardTitle></CardHeader>
              <CardContent><p className="text-sm">{briefing.relationshipHistory}</p></CardContent>
            </Card>
          )}

          {briefing.talkingPoints && briefing.talkingPoints.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-lg">Talking Points</CardTitle></CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {briefing.talkingPoints.map((point, i) => (
                    <li key={i} className="flex gap-3 text-sm">
                      <span className="shrink-0 font-bold text-primary">{i + 1}.</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {briefing.risksAndFlags && briefing.risksAndFlags.length > 0 && (
            <Card className="border-amber-200">
              <CardHeader><CardTitle className="text-lg text-amber-700">Risks & Flags</CardTitle></CardHeader>
              <CardContent>
                <ul className="space-y-1">
                  {briefing.risksAndFlags.map((flag, i) => (
                    <li key={i} className="text-sm text-amber-700">• {flag}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {briefing.rawContext && (
            <Card>
              <CardHeader><CardTitle className="text-lg">Raw Context</CardTitle></CardHeader>
              <CardContent>
                <pre className="text-xs whitespace-pre-wrap bg-muted p-4 rounded">{briefing.rawContext}</pre>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
